import React, { type ReactNode } from 'react';
import cn from 'classnames';

import { isWebGlSupported } from 'src/utils/mapUtils';
import type { YMap as YMapType } from '@yandex/ymaps3-types';
import { useTranslation } from '@lt/localization';

import { loadReactComponent } from './loaders/componentLoaders';
import EmptyBlock from '../../routes/Hotel/components/EmptyBlock';
import { loadModulePromise } from './mapLoader';
import Layers from './components/Layers';

import { DEFAULT_EASING_DURATION, MAP_CONTAINER_TEST_ID } from './constants';
import type { Coordinates } from './types';
import styles from './yandexMap.module.css';

// Ждем загрузку модулей и загружаем необходимый компонент
const YMap = loadReactComponent('YMap', loadModulePromise);

type YandexMapProps = {
  isMobile: boolean;
  center?: Coordinates;
  bounds?: [Coordinates, Coordinates];
  zoom: number;
  children?: ReactNode;
  mapRef?: React.MutableRefObject<YMapType | null>;
};

/**
 * Общий компонент Яндекс.Карт
 */
const YandexMap = ({
  center,
  bounds,
  zoom,
  children,
  isMobile,
  mapRef,
}: YandexMapProps) => {
  const { t } = useTranslation();

  return isWebGlSupported() ? (
    <div
      className={cn(styles.mapContainer, {
        [styles.mobileMapContainer]: isMobile,
      })}
      data-testid={MAP_CONTAINER_TEST_ID}
    >
      <YMap
        ref={mapRef}
        location={{
          bounds,
          center,
          zoom,
          easing: 'ease-in-out',
          duration: DEFAULT_EASING_DURATION,
        }}
        mode="vector"
        copyrights={false}
      >
        <Layers />
        {children}
      </YMap>
    </div>
  ) : (
    <EmptyBlock
      isMobile
      title={t('map.static.message.title')}
      description={t('map.static.message.description')}
    />
  );
};

export default YandexMap;
